import * as React from 'react';

import { useSiteMetadata } from '../../utils/useSiteMetadata';
import { BlogPostHero } from '../BlogPostHero/BlogPostHero';
import { Markdown } from '../Markdown/Markdown';
import { EmailNewsletterForm } from '../EmailNewsletterForm/EmailNewsletterForm';
import { ConsultationCtaMini } from '../ConsultationCtaMini/ConsultationCtaMini';

interface Props {
  authors: readonly Queries.authorFieldsFragment[];
  body: string;
  title: string;
  tags: CustomTypes.Tags;
  coverImage?: Queries.BlogPostPageEntryFragment['coverImage'];
  coverVideo?: Queries.BlogPostPageEntryFragment['coverVideo'];
  coverVideoLoop?: boolean | null;
  bgImages?: {
    default: Queries.BlogPostPageEntryFragment['bgImage'];
    original: {
      desktop: Queries.BlogPostPageEntryFragment['bgImageOriginal'];
      mobile: Queries.BlogPostPageEntryFragment['bgImageMobileOriginal'];
    };
  };
  isBgFullSize?: boolean | null;
  bgColorOverflow?: boolean | null;
  createdAt: string;
  dateISO: string;
  description: string;
  translations: Queries.BlogPostPageEntryFragment['translations'];
  relatedPosts: CustomTypes.Edges<Queries.blogPostFancyListFieldsFragment>;
  whiteBg: boolean;
  clients?: Queries.BlogPostPageEntryFragment['clients'];
  opensourceProjects?: Queries.opensourceProjectFancyListFieldsFragment[];
  products?: Queries.productFancyListFieldsFragment[];
  snippet: CustomTypes.BlogPostSnippet;
  events?: CustomTypes.Edges<Queries.eventFancyListFieldsFragment>;
  customCss?: string | null;
  cta?: Queries.BlogPostPageEntryFragment['cta'];
}

export const BlogPostPage = ({
  authors,
  body,
  cta,
  title,
  tags,
  coverImage,
  coverVideo,
  coverVideoLoop,
  bgImages,
  isBgFullSize,
  bgColorOverflow,
  createdAt,
  dateISO,
  translations,
  whiteBg,
  opensourceProjects = [],
  description,
  snippet,
  customCss,
}: Props) => {
  const { calCom } = useSiteMetadata();

  const renderCta = () => {
    const { kind, description, calComLink, buttonText } = cta || {};

    switch (kind) {
      case 'consultation':
        return (
          <ConsultationCtaMini
            openEventName="book-call-open-widget-cta"
            scrollEventName="book-call-view-widget-cta"
            id="contact-us"
            data-theme-reset
            description={description}
            calComLink={calComLink || calCom}
            buttonText={buttonText}
            textLg
            needEmail
            style={{ marginTop: '48px' }}
          />
        );
      case 'none':
        return null;
      default:
        return <EmailNewsletterForm type="post" data-theme-reset />;
    }
  };

  return (
    <>
      {customCss && (
        <style dangerouslySetInnerHTML={{ __html: customCss }}></style>
      )}

      <article>
        <BlogPostHero
          authors={authors}
          title={title}
          tags={tags}
          coverImage={coverImage}
          coverVideo={coverVideo}
          coverVideoLoop={coverVideoLoop}
          bgImages={bgImages}
          isBgFullSize={isBgFullSize}
          bgColorOverflow={bgColorOverflow}
          date={createdAt}
          dateISO={dateISO}
          translations={translations}
          whiteBg={whiteBg}
          snippet={snippet}
          description={description}
          opensourceProjects={opensourceProjects}
        />

        <Markdown body={body} data-theme-reset />
      </article>

      {renderCta()}
    </>
  );
};
